// src/Components/Filter/FilterPanel.js

import React, { useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';

// Footer Icons
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import CircleIcon from '@mui/icons-material/Circle';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';

// Action Icons
import CheckCircleIcon from '@mui/icons-material/CheckCircle'; // Tick

// Custom Icons
import { MenuResetView } from '../../assets/icons/CustomIconsAdd4';

// Firebase for permissions
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../../Firebase/firebase';

// Hook
import { useFilterManage } from '../../Hooks/useFilterManage';

// Sub-components
import FilterRegions from './FilterRegions';
import FilterCategories from './FilterCategories';
import FilterInfo from './FilterInfo';

// Shared
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import AdminBg from '../../assets/images/AdminBackground.png';

export default function FilterPanel({ onClose, onApplyFilter, initialFilterState }) {
  const { state, actions } = useFilterManage(initialFilterState);

  // --- LOCAL STATE ---
  const [userRole, setUserRole] = useState('GUEST'); // 'ADMIN', 'PERMITTED', 'GUEST'
  const [canUpdateMarkers, setCanUpdateMarkers] = useState(false); // AMENDED: Track specific update permissions
  const [loadingPerms, setLoadingPerms] = useState(true);

  // Page State (Footer Navigation)
  // AMENDED: Default safe start includes REGIONS for public access
  const [activePage, setActivePage] = useState('REGIONS');
  const [pageSteps, setPageSteps] = useState(['REGIONS', 'CATEGORIES', 'INFO']);

  // --- DRAG AND DROP STATE ---
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDraggingState, setIsDraggingState] = useState(false);
  const isDraggingRef = useRef(false);
  const startPosRef = useRef({ x: 0, y: 0 });

  // --- 1. DETERMINE USER ROLE & CONFIGURE STEPS ---
  useEffect(() => {
    const checkPermissions = async () => {
      const user = auth.currentUser;
      if (!user) {
        setUserRole('GUEST');
        // AMENDED: Guests get all filter pages
        setPageSteps(['REGIONS', 'CATEGORIES', 'INFO']);
        setActivePage('REGIONS');
        setLoadingPerms(false);
        return;
      }

      try {
        const emailKey = user.email.toLowerCase().trim();
        const docRef = doc(db, 'permittedUsers', emailKey);
        const snapshot = await getDoc(docRef);

        if (snapshot.exists()) {
          const data = snapshot.data();
          setCanUpdateMarkers(!!data.canUpdateMarkers); // AMENDED: Set update permission boolean

          if (data.isAdmin) {
            setUserRole('ADMIN');
            setPageSteps(['REGIONS', 'CATEGORIES', 'INFO']);
            setActivePage('REGIONS');
          } else {
            setUserRole('PERMITTED');
            // AMENDED: Permitted users get all filter pages
            setPageSteps(['REGIONS', 'CATEGORIES', 'INFO']);
            setActivePage('REGIONS');
          }
        } else {
          setUserRole('GUEST');
          // AMENDED: Guests get all filter pages
          setPageSteps(['REGIONS', 'CATEGORIES', 'INFO']);
          setActivePage('REGIONS');
        }
      } catch (err) {
        console.error('Error checking permissions:', err);
        setUserRole('GUEST');
        // AMENDED: Fallback error guests get all filter pages
        setPageSteps(['REGIONS', 'CATEGORIES', 'INFO']);
        setActivePage('REGIONS');
      } finally {
        setLoadingPerms(false);
      }
    };

    checkPermissions();
  }, []);

  // --- DRAG LOGIC ---
  const handlePointerDown = (e) => {
    if (e.target.closest('button')) return;

    isDraggingRef.current = true;
    setIsDraggingState(true);
    startPosRef.current = {
      x: e.clientX - offset.x,
      y: e.clientY - offset.y,
    };
    document.body.style.userSelect = 'none';
  };

  useEffect(() => {
    const handlePointerMove = (e) => {
      if (!isDraggingRef.current) return;

      let newX = e.clientX - startPosRef.current.x;
      let newY = e.clientY - startPosRef.current.y;

      if (newY < -120) newY = -120;

      setOffset({
        x: newX,
        y: newY,
      });
    };

    const handlePointerUp = () => {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
      setIsDraggingState(false);
      document.body.style.userSelect = '';
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [offset]);

  // --- 2. DATA FILTERING ---
  const isArchivedMode = state.activeSubmenu === 'ARCHIVED';

  const filteredRegions = state.allRegions.filter((r) => {
    if (userRole !== 'ADMIN') return !r.isArchived;
    return isArchivedMode ? r.isArchived : !r.isArchived;
  });

  const filteredCategories = state.allCategories.filter((c) => {
    if (userRole !== 'ADMIN') return !c.isArchived;
    return isArchivedMode ? c.isArchived : !c.isArchived;
  });

  // --- 3. HANDLERS ---
  const handleTick = () => {
    if (onApplyFilter) {
      onApplyFilter(state.filterState);
    }
  };

  const handleDelete = () => {
    actions.setShowClearAlert(true);
  };

  const handleClearConfirm = () => {
    actions.handleClear();
    if (onApplyFilter) onApplyFilter(null);
  };

  const handleNavFirst = () => {
    if (pageSteps.length > 0) {
      setActivePage(pageSteps[0]);
    }
  };

  const handleNavBack = () => {
    const currentIndex = pageSteps.indexOf(activePage);
    if (currentIndex > 0) {
      setActivePage(pageSteps[currentIndex - 1]);
    }
  };

  const handleNavForward = () => {
    const currentIndex = pageSteps.indexOf(activePage);
    if (currentIndex < pageSteps.length - 1) {
      setActivePage(pageSteps[currentIndex + 1]);
    }
  };

  const handleNavLast = () => {
    if (pageSteps.length > 0) {
      setActivePage(pageSteps[pageSteps.length - 1]);
    }
  };

  // --- 4. RENDER CONTENT PAGE ---
  const renderPage = () => {
    switch (activePage) {
      case 'REGIONS':
        return (
          <FilterRegions
            filterState={state.filterState}
            availableRegions={filteredRegions}
            onToggle={(id) => actions.toggleSelection('regionIds', id)}
            onToggleAll={(checked) =>
              actions.toggleAll(
                'regionIds',
                filteredRegions.map((r) => r.id),
                checked,
              )
            }
          />
        );
      case 'CATEGORIES':
        return (
          <FilterCategories
            filterState={state.filterState}
            availableCategories={filteredCategories}
            onToggle={(id) => actions.toggleSelection('categoryIds', id)}
            onToggleAll={(checked) =>
              actions.toggleAll(
                'categoryIds',
                filteredCategories.map((c) => c.id),
                checked,
              )
            }
          />
        );
      case 'INFO':
        return (
          <FilterInfo
            filterState={state.filterState}
            actions={actions}
            showInfoFilter={true}
            activeSubmenu={state.activeSubmenu}
            canUpdateMarkers={canUpdateMarkers}
            allBatches={state.allBatches}
          />
        );
      default:
        return null;
    }
  };

  if (loadingPerms || state.loading) {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: '120px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 100002, // AMENDED: Elevated to Processing Overlay tier
          bgcolor: 'white',
          p: 4,
          borderRadius: 2,
        }}>
        <CircularProgress />
      </Box>
    );
  }

  const sidebarItems = [];
  if (userRole === 'ADMIN') {
    sidebarItems.push({ id: 'ACTIVE', label: 'Active' });
    sidebarItems.push({ id: 'ARCHIVED', label: 'Archived' });
  }

  const panelWidth = userRole === 'ADMIN' ? '800px' : '650px';

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          bgcolor: 'rgba(0,0,0,0.2)',
          zIndex: 100001, // AMENDED: Elevated to Admin Panel Backdrop tier
          cursor: 'default',
        }}
      />

      <Box
        sx={{
          position: 'fixed',
          top: '120px',
          left: '50%',
          transform: `translate(calc(-50% + ${offset.x}px), ${offset.y}px)`,
          zIndex: 100002, // AMENDED: Elevated to Admin Panel Content tier
        }}>
        <Paper
          elevation={0}
          sx={{
            width: panelWidth,
            height: '600px',
            borderRadius: '12px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'white',
            border: '1px solid rgba(0,0,0,0.1)',
            transition: 'width 0.3s ease',
            boxShadow: '8px 0px 12px -1px rgba(0,0,0,0.5)',
          }}>
          <Box
            onPointerDown={handlePointerDown}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 3,
              py: 0.5,
              minHeight: '40px',
              borderBottom: '1px solid black',
              backgroundImage: `linear-gradient(rgba(184, 231, 253, .85), rgba(184, 231, 253, .85)), url(${AdminBg})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              color: 'white',
              flexShrink: 0,
              cursor: isDraggingState ? 'grabbing' : 'grab',
            }}>
            <Typography
              variant='h6'
              fontWeight='bold'
              sx={{
                fontSize: '1.2rem',
                color: 'black',
                pointerEvents: 'none',
              }}>
              Applied Filter
            </Typography>
            <IconButton
              onClick={onClose}
              onPointerDown={(e) => e.stopPropagation()}
              aria-label='close'
              size='small'
              sx={{ color: 'black', padding: 0 }}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
            {userRole === 'ADMIN' && (
              <Box
                sx={{
                  width: '150px',
                  bgcolor: '#707070',
                  flexShrink: 0,
                  py: 0,
                }}>
                <List component='nav' disablePadding>
                  {sidebarItems.map((item) => (
                    <ListItemButton
                      key={item.id}
                      selected={state.activeSubmenu === item.id}
                      onClick={() => actions.setActiveSubmenu(item.id)}
                      sx={{
                        color: 'white',
                        borderBottom: '1px solid white',
                        '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)' },
                        '&.Mui-selected': {
                          bgcolor: '#e3f2fd',
                          '& .MuiTypography-root': {
                            fontWeight: 600,
                            color: '#2c23e0',
                          },
                          '&:hover': { bgcolor: '#bbdefb' },
                        },
                      }}>
                      <ListItemText
                        primary={
                          <Typography sx={{ fontSize: '0.9rem' }}>
                            {item.label}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Box>
            )}

            {userRole === 'ADMIN' && (
              <Divider orientation='vertical' flexItem sx={{ borderColor: 'black' }} />
            )}

            <Box
              sx={{
                flexGrow: 1,
                bgcolor: '#fff',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                width: '650px',
              }}>
              <Box
                sx={{
                  height: '50px',
                  borderBottom: '1px solid #e0e0e0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  px: 2,
                  bgcolor: '#f5f5f5',
                  flexShrink: 0,
                }}>
                <IconButton onClick={handleDelete} title='Clear Filter' sx={{ mr: 1 }}>
                  <MenuResetView sx={{ fontSize: 32 }} />
                </IconButton>
                <IconButton onClick={handleTick} title='Apply Filter'>
                  <CheckCircleIcon sx={{ fontSize: 32, color: '#4caf50' }} />
                </IconButton>
              </Box>

              <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>{renderPage()}</Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  px: 3,
                  py: 0.5,
                  minHeight: '40px',
                  borderTop: '1px solid black',
                  backgroundImage: `linear-gradient(rgba(184, 231, 253, .85), rgba(184, 231, 253, .85)), url(${AdminBg})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  flexShrink: 0,
                  gap: 1,
                  overflow: 'visible',
                  position: 'relative',
                }}>
                <Box
                  sx={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    display: 'flex',
                    gap: 1,
                  }}>
                  {pageSteps.map((step) =>
                    activePage === step ? (
                      <CircleIcon
                        key={step}
                        sx={{ fontSize: '14px', color: '#0055ff' }}
                      />
                    ) : (
                      <CircleOutlinedIcon
                        key={step}
                        sx={{ fontSize: '14px', color: '#666' }}
                      />
                    ),
                  )}
                </Box>

                <Box sx={{ marginLeft: 'auto', display: 'flex', gap: 1 }}>
                  <IconButton
                    onClick={handleNavFirst}
                    disabled={pageSteps.indexOf(activePage) === 0}
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.3)',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.6)' },
                      '&.Mui-disabled': { opacity: 0.3 },
                    }}>
                    <FirstPageIcon
                      sx={{
                        color: pageSteps.indexOf(activePage) === 0 ? 'grey' : 'black',
                      }}
                    />
                  </IconButton>

                  <IconButton
                    onClick={handleNavBack}
                    disabled={pageSteps.indexOf(activePage) === 0}
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.3)',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.6)' },
                      '&.Mui-disabled': { opacity: 0.3 },
                    }}>
                    <ArrowBackIosNewIcon
                      sx={{
                        color: pageSteps.indexOf(activePage) === 0 ? 'grey' : 'black',
                      }}
                    />
                  </IconButton>

                  <IconButton
                    onClick={handleNavForward}
                    disabled={pageSteps.indexOf(activePage) === pageSteps.length - 1}
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.3)',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.6)' },
                      '&.Mui-disabled': { opacity: 0.3 },
                    }}>
                    <ArrowForwardIosIcon
                      sx={{
                        color:
                          pageSteps.indexOf(activePage) === pageSteps.length - 1
                            ? 'grey'
                            : 'black',
                      }}
                    />
                  </IconButton>

                  <IconButton
                    onClick={handleNavLast}
                    disabled={pageSteps.indexOf(activePage) === pageSteps.length - 1}
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.3)',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.6)' },
                      '&.Mui-disabled': { opacity: 0.3 },
                    }}>
                    <LastPageIcon
                      sx={{
                        color:
                          pageSteps.indexOf(activePage) === pageSteps.length - 1
                            ? 'grey'
                            : 'black',
                      }}
                    />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Box>

      <ConfirmDialog
        open={state.showClearAlert}
        onClose={() => actions.setShowClearAlert(false)}
        onConfirm={handleClearConfirm}
        message='This will clear all filter settings and return to default. Are you sure?'
        severity='warning'
        sx={{ zIndex: 100005 }} // AMENDED: Elevated to Dialog tier
      />
    </>
  );
}
